import type { ContentKey, IneligibleCode } from '@nvd/claim-shared';
import { StepLayout } from '../components/StepLayout';
import { formatDate } from '../lib/format';
import { usePortal } from '../Portal';

const INELIGIBLE: Record<IneligibleCode, ContentKey> = {
  NOT_PROTECTED: 'error.notProtected',
  REFUNDED: 'error.refunded',
  NOT_FULFILLED: 'error.notFulfilled',
  NOT_DELIVERED: 'error.notDelivered',
  CLAIM_WINDOW_EXPIRED: 'error.windowExpired',
};

/** The contact line is one copy string, so the address is linked wherever the merchant put it. */
function ContactLine({ email }: { email: string }) {
  const { t } = usePortal();
  const [before, after] = t('status.contact', { email }).split(email);
  return (
    <p className="nvd-claim-portal__note">
      {before}
      <a href={`mailto:${email}`}>{email}</a>
      {after}
    </p>
  );
}

/** Reached when the order already has a claim, or cannot be claimed at all. Either way it is a dead end. */
export function ClaimStatus() {
  const { config, t, state, dispatch } = usePortal();
  const view = state.view;
  if (!view) return null;

  const claim = view.existingClaim;
  const email = config.brand.supportEmail;
  const ineligible = view.eligibility.ok ? null : t(INELIGIBLE[view.eligibility.code]);

  return (
    <StepLayout
      title={claim ? t('status.title') : t('status.ineligibleTitle')}
      paragraph={claim ? t('status.body', { order: view.order.name }) : (ineligible ?? undefined)}
      onNext={() => dispatch({ type: 'RESET' })}
      nextLabel={t('common.startOver')}
    >
      {claim && (
        <dl className="nvd-claim-portal__summary">
          <div className="nvd-claim-portal__summary-row">
            <dt>{t('status.statusLabel')}</dt>
            <dd>
              <span className="nvd-claim-portal__badge">{claim.status}</span>
            </dd>
          </div>
          {claim.submittedAt && (
            <div className="nvd-claim-portal__summary-row">
              <dt>{t('status.submittedLabel')}</dt>
              <dd>{formatDate(claim.submittedAt)}</dd>
            </div>
          )}
          {claim.reason && (
            <div className="nvd-claim-portal__summary-row">
              <dt>{t('status.issueLabel')}</dt>
              <dd>{claim.reason}</dd>
            </div>
          )}
        </dl>
      )}

      {email && <ContactLine email={email} />}
    </StepLayout>
  );
}
